import { useEffect, useMemo } from 'react';
import { useUserStore } from '../stores/userStore';
import { useQuestStore } from '../stores/questStore';
import { useAssessmentStore } from '../stores/assessmentStore';
import { firstLifeMapCompletion } from '../constants/lifeMap';
import {
  deriveAllDomainReadiness,
  type DomainReadiness, type DomainReadinessSignals,
} from '../constants/domainReadiness';

/**
 * Where each domain stands on the readiness ladder — the one live read of
 * `constants/domainReadiness.ts`.
 *
 * ⚠️ Everything here is DERIVED except the `discovered` marker. Nothing about a
 * domain's readiness is written down at the moment it changes, exactly as no
 * chapter's completion is: adding a quest activates its domain because the quest
 * exists, not because anything recorded an activation.
 */
/**
 * Whether a Life Map sitting has ever been completed — Ch4's completion signal
 * and readiness's `mapped` rung.
 *
 * ⚠️⚠️ **TWO SOURCES, AND THE DURABLE ONE IS THE RECORD** (owner, 2026-09-11).
 * `userStore.lifeMapCompletedAt` answers it outright; `attemptHistory` is
 * consulted only as EVIDENCE, to backfill that field for the accounts that
 * mapped before it existed. Asking the log alone meant clearing assessment
 * history un-mapped every domain without a Quest — off the Build page, step 3
 * un-completed — and the log is truncated to the last 50 besides.
 *
 * ⚠️ `latestResults` — the only place the map's actual readings live — is an
 * in-memory cache that only the Assessment and Profile tabs populate, so a
 * Roadmap-side reader would find it empty and quietly report that nobody has
 * ever mapped their life. No caller here needs the readings, only their
 * existence.
 */
export function useHasLifeMap(): boolean {
  const attemptHistory = useAssessmentStore(s => s.attemptHistory);
  const completedAt = useUserStore(s => s.lifeMapCompletedAt);

  const evidence = useMemo(() => firstLifeMapCompletion(attemptHistory), [attemptHistory]);

  /**
   * ⭐⭐ **THE BACKFILL.** Every account that completed the Life Map before the
   * durable field shipped has the evidence and not the record; this is the one
   * moment the two can be reconciled, because it is the only place both are in
   * hand. `markLifeMapCompleted` is first-write-wins and returns without a
   * `set` when the field is already there, so this is inert from the second
   * render onward and puts nothing on the wire.
   *
   * ⚠️ Deliberately an effect and not a render-time write: a store write during
   * render re-enters every subscriber mid-commit.
   */
  useEffect(() => {
    if (evidence && !completedAt) useUserStore.getState().markLifeMapCompleted(evidence);
  }, [evidence, completedAt]);

  // ⚠️ `evidence` still counts on its own, for the frames before the backfill
  // lands — dropping to `false` for one paint would flicker the Build carousel.
  return Boolean(completedAt) || evidence !== null;
}

const NO_DECLARATIONS: Record<string, string | undefined> = {};

export function useDomainReadiness(domainIds: string[]): Record<string, DomainReadiness> {
  const discovered = useUserStore(s => s.domainsDiscovered);
  /* ⚠️⚠️ "I'm actively developing" WAS RETIRED (owner, 2026-10-02) with the Domain
     Progression Overview — nothing can set or clear it any more. So stored marks are
     NOT read: a mark nobody can see or remove must not hold a domain on a rung. The
     ladder now stops at Activated. The stored dates stay in the identity blob, so an
     objective "developing" rule can be built later (see `domainReadiness.ts`). */
  const declaredDeveloping = NO_DECLARATIONS;
  const quests = useQuestStore(s => s.quests);
  const hasLifeMap = useHasLifeMap();

  const signals = useMemo<DomainReadinessSignals>(() => ({
    discovered,
    declaredDeveloping,
    hasLifeMap,
    quests: quests.map(q => ({
      category: q.category,
      status: q.status,
      goals: q.goals.map(g => ({ completed: g.completed })),
    })),
  }), [discovered, declaredDeveloping, hasLifeMap, quests]);

  // ⚠️ Deliberately NOT memoised. `domainIds` is a fresh array on most renders,
  // so a dep array would either be hand-maintained (the stale-rank trap) or
  // recompute anyway. The derivation is a filter over a handful of quests.
  return deriveAllDomainReadiness(domainIds, signals);
}
